/**
 * Section Setup Wizard.
 *
 * Guided, progressive-disclosure form for creating yard sections with
 * optional location schemes. Replaces the flat "Add Section" form with
 * a friendlier flow:
 *
 *   1. Basics — name, type, color (always visible, minimal)
 *   2. Location tracking toggle — "do spots need individual addresses?"
 *   3. Location setup (if toggled on) — four paths:
 *      a. "Describe what you need" — natural-language parser
 *      b. Quick setup — prefix + count
 *      c. Template cards — one-click presets
 *      d. Existing scheme picker or full custom builder
 *   4. Live preview + per-location capacity
 *   5. Advanced options (shape, section capacity) — collapsed by default
 *
 * Creates both the section and (optionally) a new location scheme in
 * one flow, eliminating the need to bounce between Settings and Yard Map.
 */

import { useState, useMemo, useCallback } from "react";
import {
  Sparkles,
  Hash,
  LayoutGrid,
  ChevronDown,
  ChevronRight,
  Eye,
  Layers,
  Car,
  Archive,
  Thermometer,
  BookOpen,
  Grid3X3,
  Zap,
  Check,
  Info,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import {
  useLocationSchemes,
  useCreateLocationScheme,
} from "@/features/yards/api";
import LocationSchemeEditor from "@/features/yards/LocationSchemeEditor";
import { MAX_AREA_LOCATIONS } from "@/features/yards/constants";
import type { SegmentDefinition, LocationScheme } from "@/types/locations";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import Badge from "@/components/ui/Badge";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SECTION_TYPES = [
  {
    value: "zone",
    label: "Zone",
    icon: LayoutGrid,
    hint: "A broad area or region",
  },
  { value: "row", label: "Row", icon: Layers, hint: "A linear arrangement" },
  { value: "bay", label: "Bay", icon: Grid3X3, hint: "A subsection or stall" },
  {
    value: "building",
    label: "Building",
    icon: Archive,
    hint: "An enclosed structure",
  },
  { value: "parking", label: "Parking", icon: Car, hint: "Vehicle storage" },
  {
    value: "open",
    label: "Open Area",
    icon: Sparkles,
    hint: "Outdoor or uncovered",
  },
];

const SECTION_COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#84CC16",
  "#F97316",
  "#6366F1",
];

const SHAPE_TYPES = [
  { value: "rectangle", label: "Rectangle" },
  { value: "circle", label: "Circle / Ellipse" },
  { value: "l_shape", label: "L-Shape" },
  { value: "t_shape", label: "T-Shape" },
  { value: "triangle", label: "Triangle" },
];

const selectClass =
  "mt-1.5 block w-full h-9 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] px-3 text-sm";

// ---------------------------------------------------------------------------
// Template definitions — one-click presets with visual context
// ---------------------------------------------------------------------------

interface SetupTemplate {
  key: string;
  name: string;
  description: string;
  example: string;
  icon: typeof Car;
  separator: string;
  segments: SegmentDefinition[];
}

const TEMPLATES: SetupTemplate[] = [
  {
    key: "parking",
    name: "Parking Lot",
    description: "Lettered sections with numbered spaces",
    example: "A-042",
    icon: Car,
    separator: "-",
    segments: [
      { name: "Section", type: "letter", start: "A", end: "D" },
      { name: "Space", type: "number", start: 1, end: 50, padding: 3 },
    ],
  },
  {
    key: "rack",
    name: "Pallet Rack",
    description: "Aisle, bay, level, and position",
    example: "01-03-B-02",
    icon: Layers,
    separator: "-",
    segments: [
      { name: "Aisle", type: "number", start: 1, end: 10, padding: 2 },
      { name: "Bay", type: "number", start: 1, end: 8, padding: 2 },
      { name: "Level", type: "letter", start: "A", end: "E" },
      { name: "Position", type: "number", start: 1, end: 4, padding: 2 },
    ],
  },
  {
    key: "bins",
    name: "Storage Bins",
    description: "Prefixed sequential bin numbers",
    example: "BIN-0042",
    icon: Archive,
    separator: "-",
    segments: [
      { name: "Zone", type: "fixed", values: ["BIN"] },
      { name: "Bin", type: "number", start: 1, end: 200, padding: 4 },
    ],
  },
  {
    key: "shelving",
    name: "Shelving Unit",
    description: "Section, shelf, and position",
    example: "C-04-03",
    icon: BookOpen,
    separator: "-",
    segments: [
      { name: "Section", type: "letter", start: "A", end: "H" },
      { name: "Shelf", type: "number", start: 1, end: 6, padding: 2 },
      { name: "Position", type: "number", start: 1, end: 8, padding: 2 },
    ],
  },
  {
    key: "cold",
    name: "Cold Storage",
    description: "Temperature zone, area, and row",
    example: "FRZ-B-07",
    icon: Thermometer,
    separator: "-",
    segments: [
      { name: "Temp", type: "fixed", values: ["FRZ", "CHL"] },
      { name: "Zone", type: "letter", start: "A", end: "D" },
      { name: "Row", type: "number", start: 1, end: 20, padding: 2 },
    ],
  },
  {
    key: "sequential",
    name: "Simple Sequential",
    description: "Prefix and sequential number",
    example: "LOC-0001",
    icon: Hash,
    separator: "-",
    segments: [
      { name: "Prefix", type: "fixed", values: ["LOC"] },
      { name: "Number", type: "number", start: 1, end: 100, padding: 4 },
    ],
  },
];

// ---------------------------------------------------------------------------
// Natural-language parser — "describe what you need"
// ---------------------------------------------------------------------------

interface ParsedQuick {
  mode: "quick";
  count: number;
  prefix: string;
  summary: string;
}

interface ParsedSegments {
  mode: "segments";
  segments: SegmentDefinition[];
  separator: string;
  summary: string;
}

type ParseResult = ParsedQuick | ParsedSegments;

const NOUN_DEFAULTS: Record<
  string,
  { name: string; type: "number" | "letter"; prefix?: string }
> = {
  space: { name: "Space", type: "number", prefix: "P" },
  spot: { name: "Spot", type: "number", prefix: "S" },
  stall: { name: "Stall", type: "number", prefix: "S" },
  bin: { name: "Bin", type: "number", prefix: "BIN" },
  slot: { name: "Slot", type: "number", prefix: "LOC" },
  position: { name: "Position", type: "number" },
  location: { name: "Location", type: "number", prefix: "LOC" },
  unit: { name: "Unit", type: "number", prefix: "U" },
  berth: { name: "Berth", type: "number", prefix: "B" },
  dock: { name: "Dock", type: "number", prefix: "DK" },
  bay: { name: "Bay", type: "number" },
  row: { name: "Row", type: "number" },
  aisle: { name: "Aisle", type: "number" },
  rack: { name: "Rack", type: "number" },
  zone: { name: "Zone", type: "letter" },
  area: { name: "Area", type: "letter" },
  section: { name: "Section", type: "letter" },
  level: { name: "Level", type: "letter" },
  shelf: { name: "Shelf", type: "number" },
  tier: { name: "Tier", type: "letter" },
  lane: { name: "Lane", type: "number" },
};

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function parseLocationDescription(text: string): ParseResult | null {
  const lower = text.toLowerCase().trim();
  if (!lower || lower.length < 3) return null;

  // Extract "number noun" pairs in order of appearance
  const pairRegex = /(\d+)\s+([a-z]+?)s?\b/g;
  const pairs: Array<{ count: number; noun: string }> = [];
  let m;
  while ((m = pairRegex.exec(lower)) !== null) {
    pairs.push({ count: parseInt(m[1]!), noun: m[2]! });
  }

  if (pairs.length === 0) return null;

  // Single dimension: quick sequential
  if (pairs.length === 1) {
    const p = pairs[0]!;
    const info = NOUN_DEFAULTS[p.noun];
    const prefix = info?.prefix || capitalize(p.noun).slice(0, 3).toUpperCase();
    return {
      mode: "quick",
      count: p.count,
      prefix,
      summary: `${p.count} ${info?.name.toLowerCase() || p.noun}s`,
    };
  }

  // Multi-dimension: build segments from pairs
  const segments: SegmentDefinition[] = pairs.map((p) => {
    const info = NOUN_DEFAULTS[p.noun];
    const segName = info?.name || capitalize(p.noun);
    const segType = info?.type || "number";
    if (segType === "letter") {
      const endChar = String.fromCharCode(64 + Math.min(p.count, 26));
      return {
        name: segName,
        type: "letter" as const,
        start: "A",
        end: endChar,
      };
    }
    const pad = p.count >= 1000 ? 4 : p.count >= 100 ? 3 : 2;
    return {
      name: segName,
      type: "number" as const,
      start: 1,
      end: p.count,
      padding: pad,
    };
  });

  const total = segments.reduce((acc, seg) => {
    if (seg.type === "letter") {
      const s = String(seg.start ?? "A").charCodeAt(0);
      const e = String(seg.end ?? "A").charCodeAt(0);
      return acc * (e - s + 1);
    }
    if (seg.type === "number") {
      return acc * ((seg.end as number) - (seg.start as number) + 1);
    }
    return acc * (seg.values?.length || 1);
  }, 1);

  const parts = pairs.map((p) => {
    const info = NOUN_DEFAULTS[p.noun];
    return `${p.count} ${info?.name.toLowerCase() || p.noun}s`;
  });

  return {
    mode: "segments",
    segments,
    separator: "-",
    summary: `${parts.join(" × ")} = ${total.toLocaleString()} locations`,
  };
}

// ---------------------------------------------------------------------------
// Label generator (client-side preview)
// ---------------------------------------------------------------------------

function expandSegment(seg: SegmentDefinition): string[] {
  const prefix = seg.prefix || "";
  if (seg.type === "number") {
    const start =
      typeof seg.start === "number"
        ? seg.start
        : parseInt(String(seg.start ?? "1"));
    const end =
      typeof seg.end === "number" ? seg.end : parseInt(String(seg.end ?? "1"));
    const pad = seg.padding ?? 0;
    const out: string[] = [];
    for (let i = start; i <= end; i++)
      out.push(`${prefix}${String(i).padStart(pad, "0")}`);
    return out;
  }
  if (seg.type === "letter") {
    const s = String(seg.start ?? "A")
      .toUpperCase()
      .charCodeAt(0);
    const e = String(seg.end ?? "A")
      .toUpperCase()
      .charCodeAt(0);
    const out: string[] = [];
    for (let c = s; c <= e; c++) out.push(`${prefix}${String.fromCharCode(c)}`);
    return out;
  }
  if (seg.type === "fixed")
    return (seg.values ?? []).map((v) => `${prefix}${v}`);
  return [];
}

function generatePreviewLabels(
  segments: SegmentDefinition[],
  separator: string,
): string[] {
  if (segments.length === 0) return [];
  const expanded = segments.map(expandSegment);
  if (expanded.some((e) => e.length === 0)) return [];
  const MAX = 10001;
  const result: string[] = [];
  const indices = new Array(expanded.length).fill(0) as number[];
  while (result.length < MAX) {
    result.push(indices.map((idx, i) => expanded[i]![idx]!).join(separator));
    let carry = true;
    for (let i = indices.length - 1; i >= 0 && carry; i--) {
      indices[i]!++;
      if (indices[i]! < expanded[i]!.length) carry = false;
      else indices[i] = 0;
    }
    if (carry) break;
  }
  return result;
}

function countLocations(segments: SegmentDefinition[]): number {
  if (segments.length === 0) return 0;
  return segments.reduce((acc, seg) => acc * expandSegment(seg).length, 1);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PreviewPanel({
  segments,
  separator,
}: {
  segments: SegmentDefinition[];
  separator: string;
}) {
  const labels = useMemo(
    () => generatePreviewLabels(segments, separator),
    [segments, separator],
  );
  const total = useMemo(() => countLocations(segments), [segments]);

  if (total === 0) return null;

  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-sunken)] p-3">
      <div className="flex items-center gap-2 mb-2">
        <Eye className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
        <span className="text-xs font-semibold text-[var(--text-primary)]">
          Preview
        </span>
        <Badge variant={total <= MAX_AREA_LOCATIONS ? "info" : "danger"}>
          {total.toLocaleString()} location{total !== 1 ? "s" : ""}
        </Badge>
      </div>
      {total > MAX_AREA_LOCATIONS && (
        <p className="text-xs text-[var(--text-danger)] mb-2">
          Exceeds the {MAX_AREA_LOCATIONS.toLocaleString()} location maximum.
          Reduce your ranges.
        </p>
      )}
      <div className="flex flex-wrap gap-1">
        {labels.slice(0, 15).map((label, i) => (
          <span
            key={i}
            className="inline-flex items-center rounded-md bg-[var(--surface-base)] px-2 py-0.5 text-[11px] font-mono text-[var(--text-secondary)] border border-[var(--border-subtle)]"
          >
            {label}
          </span>
        ))}
        {total > 20 && (
          <span className="inline-flex items-center px-2 py-0.5 text-[11px] text-[var(--text-tertiary)]">
            … {(total - 20).toLocaleString()} more …
          </span>
        )}
        {total > 20 &&
          labels.slice(-5).map((label, i) => (
            <span
              key={`last-${i}`}
              className="inline-flex items-center rounded-md bg-[var(--surface-base)] px-2 py-0.5 text-[11px] font-mono text-[var(--text-secondary)] border border-[var(--border-subtle)]"
            >
              {label}
            </span>
          ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-area range overrides
// ---------------------------------------------------------------------------

interface SegmentOverride {
  index: number;
  start?: number | string;
  end?: number | string;
}

/**
 * Build per-area start/end overrides from the range-carrying segments
 * (number/letter). Schemes are structure-only, so these ranges are what the
 * apply step uses to generate a specific area's locations.
 */
function buildOverrides(segments: SegmentDefinition[]): SegmentOverride[] {
  return segments
    .map((seg, index) => ({ seg, index }))
    .filter(({ seg }) => seg.type === "number" || seg.type === "letter")
    .map(({ seg, index }) => ({ index, start: seg.start, end: seg.end }));
}

// ---------------------------------------------------------------------------
// Main Wizard Component
// ---------------------------------------------------------------------------

type LocationMode = "quick" | "template" | "existing" | "custom";

interface SectionSetupWizardProps {
  /** Yard map ID — when creating a section via the map editor. */
  yardId?: string;
  /** Business location ID — when creating a standalone storage area. */
  businessLocationId?: string;
  existingSectionCount?: number;
  onCreated: () => void;
  onCancel?: () => void;
}

function SectionSetupWizard({
  yardId,
  businessLocationId,
  existingSectionCount = 0,
  onCreated,
  onCancel,
}: SectionSetupWizardProps) {
  const isStandalone = !!businessLocationId && !yardId;
  const qc = useQueryClient();
  const { data: schemes } = useLocationSchemes();
  const createSchemeMutation = useCreateLocationScheme();

  // --- Basics ---
  const [label, setLabel] = useState("");
  const [sectionType, setSectionType] = useState("zone");
  const [color, setColor] = useState(
    SECTION_COLORS[existingSectionCount % SECTION_COLORS.length]!,
  );
  const [shape, setShape] = useState("rectangle");
  const [sectionCapacity, setSectionCapacity] = useState("");

  const [needsLocations, setNeedsLocations] = useState(false);

  // --- Location setup mode ---
  const [locationMode, setLocationMode] = useState<LocationMode>("quick");
  const [describeText, setDescribeText] = useState("");

  // Quick mode
  const [quickPrefix, setQuickPrefix] = useState("LOC");
  const [quickCount, setQuickCount] = useState("50");
  const [quickPadding, setQuickPadding] = useState(3);

  // Template mode
  const [activeTemplate, setActiveTemplate] = useState<string | null>(null);
  const [templateSegments, setTemplateSegments] = useState<SegmentDefinition[]>(
    [],
  );
  const [templateSeparator, setTemplateSeparator] = useState("-");

  // Existing scheme
  const [existingSchemeId, setExistingSchemeId] = useState("");

  // Custom mode
  const [customSegments, setCustomSegments] = useState<SegmentDefinition[]>([]);
  const [customSeparator, setCustomSeparator] = useState("-");
  const [showCustomEditor, setShowCustomEditor] = useState(false);

  // Common
  const [perLocationCapacity, setPerLocationCapacity] = useState("1");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // --- Describe parser ---
  const parseResult = useMemo(
    () => parseLocationDescription(describeText),
    [describeText],
  );

  const applyParseResult = useCallback(() => {
    if (!parseResult) return;
    if (parseResult.mode === "quick") {
      setLocationMode("quick");
      setQuickPrefix(parseResult.prefix);
      setQuickCount(String(parseResult.count));
      setQuickPadding(
        parseResult.count >= 1000 ? 4 : parseResult.count >= 100 ? 3 : 2,
      );
    } else {
      setLocationMode("template");
      setActiveTemplate(null);
      setTemplateSegments(parseResult.segments);
      setTemplateSeparator(parseResult.separator);
    }
  }, [parseResult]);

  // --- Compute active segments for preview ---
  const { activeSegments, activeSeparator } = useMemo(() => {
    if (!needsLocations) return { activeSegments: [], activeSeparator: "-" };
    if (locationMode === "quick") {
      const count = parseInt(quickCount) || 0;
      if (count <= 0) return { activeSegments: [], activeSeparator: "-" };
      const segs: SegmentDefinition[] = [
        { name: "Prefix", type: "fixed", values: [quickPrefix || "LOC"] },
        {
          name: "Number",
          type: "number",
          start: 1,
          end: count,
          padding: quickPadding,
        },
      ];
      return { activeSegments: segs, activeSeparator: "-" };
    }
    if (locationMode === "template") {
      return {
        activeSegments: templateSegments,
        activeSeparator: templateSeparator,
      };
    }
    if (locationMode === "existing") {
      return { activeSegments: [], activeSeparator: "-" };
    }
    if (locationMode === "custom") {
      return {
        activeSegments: customSegments,
        activeSeparator: customSeparator,
      };
    }
    return { activeSegments: [], activeSeparator: "-" };
  }, [
    needsLocations,
    locationMode,
    quickPrefix,
    quickCount,
    quickPadding,
    templateSegments,
    templateSeparator,
    customSegments,
    customSeparator,
  ]);

  const totalLocations = useMemo(
    () => countLocations(activeSegments),
    [activeSegments],
  );

  // --- Template selection ---
  const applyTemplate = useCallback((tmpl: SetupTemplate) => {
    setActiveTemplate(tmpl.key);
    setTemplateSegments(tmpl.segments.map((s) => ({ ...s })));
    setTemplateSeparator(tmpl.separator);
    setLocationMode("template");
  }, []);

  // --- Validation ---
  const isValid = useMemo(() => {
    if (!label.trim()) return false;
    if (!needsLocations) return true;
    if (locationMode === "existing") return !!existingSchemeId;
    if (locationMode === "custom")
      return (
        customSegments.length > 0 &&
        countLocations(customSegments) > 0 &&
        countLocations(customSegments) <= MAX_AREA_LOCATIONS
      );
    return totalLocations > 0 && totalLocations <= MAX_AREA_LOCATIONS;
  }, [
    label,
    needsLocations,
    locationMode,
    existingSchemeId,
    customSegments,
    totalLocations,
  ]);

  // --- Create section / storage area ---
  const addSectionMutation = useMutation({
    mutationFn: async ({
      schemeId,
      overrides,
    }: {
      schemeId?: string;
      overrides?: SegmentOverride[];
    }) => {
      // When ranges are supplied, the backend generates the area's locations;
      // otherwise the scheme is only assigned (generate later).
      const segment_overrides =
        overrides && overrides.length ? overrides : undefined;
      if (isStandalone) {
        // Standalone mode: create via storage-areas endpoint.
        return (
          await apiClient.post("/api/v1/storage-areas", {
            label,
            section_type: sectionType,
            business_location_id: businessLocationId,
            location_scheme_id: schemeId,
            capacity: sectionCapacity ? parseInt(sectionCapacity) : undefined,
            location_capacity: schemeId
              ? parseInt(perLocationCapacity) || 1
              : undefined,
            segment_overrides,
          })
        ).data;
      }
      // Map editor mode: create via yard sections endpoint.
      return (
        await apiClient.post(`/api/v1/yards/${yardId}/sections`, {
          label,
          section_type: sectionType,
          color,
          shape_type: shape,
          x: 20 + existingSectionCount * 170,
          y: 20,
          width: 150,
          height: 120,
          capacity: sectionCapacity ? parseInt(sectionCapacity) : undefined,
          location_scheme_id: schemeId || undefined,
          location_capacity: schemeId
            ? parseInt(perLocationCapacity) || 1
            : undefined,
          segment_overrides,
        })
      ).data;
    },
    onSuccess: () => {
      if (isStandalone) {
        qc.invalidateQueries({ queryKey: ["storage-areas"] });
        toast.success("Storage area created", `"${label}" has been added.`);
      } else {
        qc.invalidateQueries({ queryKey: ["yard-map", yardId] });
        qc.invalidateQueries({ queryKey: ["yards"] });
        toast.success(
          "Section created",
          `"${label}" has been added to your facility map.`,
        );
      }
      onCreated();
    },
    onError: () =>
      toast.error(
        isStandalone
          ? "Failed to create storage area"
          : "Failed to create section",
      ),
  });

  const handleCreate = async () => {
    if (!isValid) return;
    setIsCreating(true);

    try {
      if (!needsLocations) {
        addSectionMutation.mutate({});
        return;
      }

      if (locationMode === "existing") {
        // Assign the reusable template; per-area ranges are entered when
        // generating locations (no ranges are baked into the scheme).
        addSectionMutation.mutate({ schemeId: existingSchemeId });
        return;
      }

      // Create a structure-only scheme, then create the section and generate its
      // locations from the per-area ranges (sent as segment_overrides).
      const segs = locationMode === "custom" ? customSegments : activeSegments;
      const sep = locationMode === "custom" ? customSeparator : activeSeparator;
      const overrides = buildOverrides(segs);
      const schemeName = `${label} Layout`;

      createSchemeMutation.mutate(
        { name: schemeName, segments: segs, separator: sep },
        {
          onSuccess: (created: LocationScheme) => {
            addSectionMutation.mutate({ schemeId: created.id, overrides });
          },
          onError: () => {
            toast.error("Failed to create location scheme");
            setIsCreating(false);
          },
        },
      );
    } catch {
      setIsCreating(false);
    }
  };

  const modeTabClass = (mode: LocationMode) =>
    `px-3 py-1.5 text-xs font-medium rounded-[var(--radius-md)] transition-colors ${
      locationMode === mode
        ? "bg-[var(--color-interactive-primary)] text-white"
        : "text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
    }`;

  return (
    <div className="space-y-5">
      {/* ================================================================ */}
      {/* SECTION 1: BASICS                                                */}
      {/* ================================================================ */}
      <div className="space-y-3">
        <Input
          label="Section Name"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          required
          placeholder="e.g., Parking Lot A, Building B, Staging Area"
          autoFocus
        />

        {/* Type — visual icon cards */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-1.5">
            Type
          </label>
          <div className="grid grid-cols-3 gap-1.5">
            {SECTION_TYPES.map((t) => {
              const Icon = t.icon;
              const isActive = sectionType === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setSectionType(t.value)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-[var(--radius-md)] border text-left transition-all ${
                    isActive
                      ? "border-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/5 ring-1 ring-[var(--color-interactive-primary)]/20"
                      : "border-[var(--border-subtle)] hover:border-[var(--border-default)] hover:bg-[var(--surface-sunken)]"
                  }`}
                >
                  <Icon
                    className={`h-4 w-4 shrink-0 ${isActive ? "text-[var(--color-interactive-primary)]" : "text-[var(--text-tertiary)]"}`}
                  />
                  <div>
                    <p
                      className={`text-xs font-medium ${isActive ? "text-[var(--color-interactive-primary)]" : "text-[var(--text-primary)]"}`}
                    >
                      {t.label}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Color (map editor only — standalone areas don't need visual styling) */}
        {!isStandalone && (
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-1.5">
              Color
            </label>
            <div className="flex gap-1.5 flex-wrap">
              {SECTION_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`h-7 w-7 rounded-full border-2 transition-all ${
                    color === c
                      ? "border-[var(--text-primary)] scale-110 shadow-sm"
                      : "border-transparent hover:scale-105"
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ================================================================ */}
      {/* SECTION 2: LOCATION TRACKING TOGGLE */}
      {/* ================================================================ */}
      <div className="border-t border-[var(--border-subtle)] pt-4">
        <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
          Location Tracking
        </label>
        <div className="grid grid-cols-1 gap-2">
          <button
            type="button"
            onClick={() => setNeedsLocations(false)}
            className={`flex items-start gap-3 p-3 rounded-[var(--radius-lg)] border text-left transition-all ${
              !needsLocations
                ? "border-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/5 ring-1 ring-[var(--color-interactive-primary)]/20"
                : "border-[var(--border-subtle)] hover:border-[var(--border-default)]"
            }`}
          >
            <div
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                !needsLocations
                  ? "border-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]"
                  : "border-[var(--border-default)]"
              }`}
            >
              {!needsLocations && <Check className="h-3 w-3 text-white" />}
            </div>
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">
                Simple area
              </p>
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                Assets are assigned to the section as a whole. Best for open
                lots, staging areas, or zones where you don&apos;t need to track
                exact positions.
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setNeedsLocations(true)}
            className={`flex items-start gap-3 p-3 rounded-[var(--radius-lg)] border text-left transition-all ${
              needsLocations
                ? "border-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/5 ring-1 ring-[var(--color-interactive-primary)]/20"
                : "border-[var(--border-subtle)] hover:border-[var(--border-default)]"
            }`}
          >
            <div
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                needsLocations
                  ? "border-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]"
                  : "border-[var(--border-default)]"
              }`}
            >
              {needsLocations && <Check className="h-3 w-3 text-white" />}
            </div>
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">
                Individual locations
              </p>
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                Generate labeled spots (like parking spaces, rack positions, or
                bin slots). Each location gets a unique address and capacity.
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION 3: LOCATION SETUP (only when individual locations)       */}
      {/* ================================================================ */}
      {needsLocations && (
        <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* --- Describe input --- */}
          <div className="rounded-[var(--radius-lg)] border border-brand-200 dark:border-brand-800 bg-brand-50/30 dark:bg-brand-900/10 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-brand-600 dark:text-brand-300" />
              <span className="text-xs font-semibold text-[var(--text-primary)]">
                Describe what you need
              </span>
            </div>
            <input
              value={describeText}
              onChange={(e) => setDescribeText(e.target.value)}
              placeholder='e.g., "50 parking spaces", "10 aisles with 8 bays and 5 levels", "200 bins"'
              className="w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm placeholder:text-[var(--text-tertiary)]"
            />
            {describeText.length >= 3 && (
              <div className="mt-2">
                {parseResult ? (
                  <div className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-[var(--color-success)] shrink-0" />
                    <span className="text-xs text-[var(--text-primary)]">
                      Understood: <strong>{parseResult.summary}</strong>
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      onClick={applyParseResult}
                      className="ml-auto"
                    >
                      <Zap className="h-3 w-3" /> Apply
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-[var(--text-tertiary)] flex items-center gap-1.5">
                    <Info className="h-3 w-3" />
                    Try something like "50 spots", "4 rows of 25 spaces", or "10
                    aisles, 8 bays, 5 levels"
                  </p>
                )}
              </div>
            )}
          </div>

          {/* --- Setup mode tabs --- */}
          <div>
            <div className="flex items-center gap-1 mb-3 bg-[var(--surface-sunken)] p-1 rounded-[var(--radius-lg)]">
              <button
                type="button"
                className={modeTabClass("quick")}
                onClick={() => setLocationMode("quick")}
              >
                Quick Setup
              </button>
              <button
                type="button"
                className={modeTabClass("template")}
                onClick={() => setLocationMode("template")}
              >
                Templates
              </button>
              <button
                type="button"
                className={modeTabClass("existing")}
                onClick={() => setLocationMode("existing")}
              >
                Existing Scheme
              </button>
              <button
                type="button"
                className={modeTabClass("custom")}
                onClick={() => setLocationMode("custom")}
              >
                Custom
              </button>
            </div>

            {/* QUICK MODE */}
            {locationMode === "quick" && (
              <div className="space-y-3 animate-in fade-in duration-150">
                <p className="text-xs text-[var(--text-secondary)]">
                  Generate sequentially numbered locations with a prefix.
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    label="Prefix"
                    value={quickPrefix}
                    onChange={(e) =>
                      setQuickPrefix(e.target.value.toUpperCase())
                    }
                    placeholder="LOC"
                    maxLength={10}
                  />
                  <Input
                    label="How many?"
                    type="number"
                    value={quickCount}
                    onChange={(e) => setQuickCount(e.target.value)}
                    min={1}
                    max={MAX_AREA_LOCATIONS}
                  />
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-primary)]">
                      Pad digits
                    </label>
                    <select
                      value={quickPadding}
                      onChange={(e) =>
                        setQuickPadding(parseInt(e.target.value))
                      }
                      className={selectClass}
                    >
                      <option value={0}>No padding (1, 2, 3)</option>
                      <option value={2}>2 digits (01, 02, 03)</option>
                      <option value={3}>3 digits (001, 002, 003)</option>
                      <option value={4}>4 digits (0001, 0002, 0003)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* TEMPLATE MODE */}
            {locationMode === "template" && (
              <div className="space-y-3 animate-in fade-in duration-150">
                {!activeTemplate && templateSegments.length === 0 && (
                  <p className="text-xs text-[var(--text-secondary)]">
                    Pick a starting template — you can adjust the ranges after.
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {TEMPLATES.map((tmpl) => {
                    const Icon = tmpl.icon;
                    const isActive = activeTemplate === tmpl.key;
                    return (
                      <button
                        key={tmpl.key}
                        type="button"
                        onClick={() => applyTemplate(tmpl)}
                        className={`flex items-start gap-2.5 p-3 rounded-[var(--radius-lg)] border text-left transition-all ${
                          isActive
                            ? "border-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/5 ring-1 ring-[var(--color-interactive-primary)]/20"
                            : "border-[var(--border-subtle)] hover:border-[var(--border-default)] hover:bg-[var(--surface-sunken)]"
                        }`}
                      >
                        <Icon
                          className={`h-5 w-5 mt-0.5 shrink-0 ${isActive ? "text-[var(--color-interactive-primary)]" : "text-[var(--text-tertiary)]"}`}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-xs font-semibold ${isActive ? "text-[var(--color-interactive-primary)]" : "text-[var(--text-primary)]"}`}
                            >
                              {tmpl.name}
                            </span>
                            <span className="text-[10px] font-mono text-brand-600 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/30 px-1.5 py-0.5 rounded shrink-0">
                              {tmpl.example}
                            </span>
                          </div>
                          <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
                            {tmpl.description}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Editable segment ranges for selected template */}
                {templateSegments.length > 0 && (
                  <Card padding="sm" className="bg-[var(--surface-sunken)]">
                    <p className="text-xs font-medium text-[var(--text-primary)] mb-2">
                      Adjust ranges for your space
                    </p>
                    <div className="space-y-2">
                      {templateSegments.map((seg, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="text-xs font-medium text-[var(--text-secondary)] w-16 shrink-0">
                            {seg.name}
                          </span>
                          {seg.type === "fixed" ? (
                            <input
                              value={(seg.values ?? []).join(", ")}
                              onChange={(e) => {
                                const values = e.target.value
                                  .split(",")
                                  .map((v) => v.trim())
                                  .filter(Boolean);
                                setTemplateSegments((prev) =>
                                  prev.map((s, i) =>
                                    i === idx ? { ...s, values } : s,
                                  ),
                                );
                              }}
                              placeholder="Comma-separated values"
                              className="flex-1 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
                            />
                          ) : seg.type === "letter" ? (
                            <>
                              <select
                                value={String(seg.start ?? "A")}
                                onChange={(e) =>
                                  setTemplateSegments((prev) =>
                                    prev.map((s, i) =>
                                      i === idx
                                        ? { ...s, start: e.target.value }
                                        : s,
                                    ),
                                  )
                                }
                                className="w-16 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
                              >
                                {"ABCDEFGHIJKLMNOPQRSTUVWXYZ"
                                  .split("")
                                  .map((c) => (
                                    <option key={c} value={c}>
                                      {c}
                                    </option>
                                  ))}
                              </select>
                              <span className="text-xs text-[var(--text-tertiary)]">
                                to
                              </span>
                              <select
                                value={String(seg.end ?? "Z")}
                                onChange={(e) =>
                                  setTemplateSegments((prev) =>
                                    prev.map((s, i) =>
                                      i === idx
                                        ? { ...s, end: e.target.value }
                                        : s,
                                    ),
                                  )
                                }
                                className="w-16 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
                              >
                                {"ABCDEFGHIJKLMNOPQRSTUVWXYZ"
                                  .split("")
                                  .map((c) => (
                                    <option key={c} value={c}>
                                      {c}
                                    </option>
                                  ))}
                              </select>
                              <span className="text-[10px] text-[var(--text-tertiary)] ml-auto">
                                {expandSegment(seg).length} values
                              </span>
                            </>
                          ) : (
                            <>
                              <input
                                type="number"
                                value={String(seg.start ?? "")}
                                onChange={(e) =>
                                  setTemplateSegments((prev) =>
                                    prev.map((s, i) =>
                                      i === idx
                                        ? {
                                            ...s,
                                            start:
                                              parseInt(e.target.value) || 1,
                                          }
                                        : s,
                                    ),
                                  )
                                }
                                className="w-16 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
                              />
                              <span className="text-xs text-[var(--text-tertiary)]">
                                to
                              </span>
                              <input
                                type="number"
                                value={String(seg.end ?? "")}
                                onChange={(e) =>
                                  setTemplateSegments((prev) =>
                                    prev.map((s, i) =>
                                      i === idx
                                        ? {
                                            ...s,
                                            end: parseInt(e.target.value) || 1,
                                          }
                                        : s,
                                    ),
                                  )
                                }
                                className="w-16 h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
                              />
                              <span className="text-[10px] text-[var(--text-tertiary)] ml-auto">
                                {expandSegment(seg).length} values
                              </span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            )}

            {/* EXISTING SCHEME MODE */}
            {locationMode === "existing" && (
              <div className="space-y-3 animate-in fade-in duration-150">
                <p className="text-xs text-[var(--text-secondary)]">
                  Apply a scheme you've already created. The section will use
                  the scheme's location pattern.
                </p>
                {schemes && schemes.length > 0 ? (
                  <div className="space-y-2">
                    {schemes.map((scheme) => {
                      const isActive = existingSchemeId === scheme.id;
                      return (
                        <button
                          key={scheme.id}
                          type="button"
                          onClick={() =>
                            setExistingSchemeId(isActive ? "" : scheme.id)
                          }
                          className={`flex items-center gap-3 w-full p-3 rounded-[var(--radius-md)] border text-left transition-all ${
                            isActive
                              ? "border-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/5 ring-1 ring-[var(--color-interactive-primary)]/20"
                              : "border-[var(--border-subtle)] hover:border-[var(--border-default)]"
                          }`}
                        >
                          <Grid3X3
                            className={`h-4 w-4 shrink-0 ${isActive ? "text-[var(--color-interactive-primary)]" : "text-[var(--text-tertiary)]"}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-[var(--text-primary)]">
                                {scheme.name}
                              </span>
                              <Badge
                                variant={
                                  scheme.total_locations != null
                                    ? "info"
                                    : "default"
                                }
                              >
                                {scheme.total_locations != null
                                  ? `${scheme.total_locations.toLocaleString()} locations`
                                  : "Template"}
                              </Badge>
                            </div>
                            {scheme.description && (
                              <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5 truncate">
                                {scheme.description}
                              </p>
                            )}
                          </div>
                          {isActive && (
                            <Check className="h-4 w-4 text-[var(--color-interactive-primary)] shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <Card className="border-dashed">
                    <p className="text-xs text-[var(--text-tertiary)] text-center py-4">
                      No schemes created yet. Use Quick Setup, a Template, or
                      build a Custom scheme.
                    </p>
                  </Card>
                )}
              </div>
            )}

            {/* CUSTOM MODE */}
            {locationMode === "custom" && (
              <div className="space-y-3 animate-in fade-in duration-150">
                {!showCustomEditor ? (
                  <Card className="border-dashed">
                    <div className="flex flex-col items-center py-4 text-center">
                      <Grid3X3 className="h-8 w-8 text-[var(--text-tertiary)]" />
                      <p className="text-xs text-[var(--text-secondary)] mt-2">
                        Build a fully custom location scheme with multiple
                        segment types and advanced options.
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        className="mt-3"
                        onClick={() => setShowCustomEditor(true)}
                      >
                        Open Scheme Builder
                      </Button>
                    </div>
                  </Card>
                ) : (
                  <LocationSchemeEditor
                    onSave={(data) => {
                      setCustomSegments(data.segments);
                      setCustomSeparator(data.separator);
                      setShowCustomEditor(false);
                    }}
                    onCancel={() => setShowCustomEditor(false)}
                  />
                )}
                {customSegments.length > 0 && !showCustomEditor && (
                  <div className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-[var(--color-success)]" />
                    <span className="text-xs text-[var(--text-primary)]">
                      Custom scheme configured (
                      {countLocations(customSegments).toLocaleString()}{" "}
                      locations)
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowCustomEditor(true)}
                      className="text-xs text-brand-600 hover:underline ml-auto"
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* --- Preview --- */}
          {activeSegments.length > 0 && (
            <PreviewPanel
              segments={activeSegments}
              separator={activeSeparator}
            />
          )}

          {/* --- Per-location capacity --- */}
          {(activeSegments.length > 0 ||
            (locationMode === "existing" && existingSchemeId)) && (
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Input
                  label="Capacity per location"
                  type="number"
                  value={perLocationCapacity}
                  onChange={(e) => setPerLocationCapacity(e.target.value)}
                  min={1}
                  max={MAX_AREA_LOCATIONS}
                />
              </div>
              <p className="text-[10px] text-[var(--text-tertiary)] mt-6 flex-1">
                How many assets can each location hold. Set to 1 for parking
                spaces, higher for shelves or bins.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* ADVANCED OPTIONS                                                 */}
      {/* ================================================================ */}
      <div className="border-t border-[var(--border-subtle)] pt-3">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          {showAdvanced ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
          Advanced options
        </button>
        {showAdvanced && (
          <div className="mt-3 grid grid-cols-2 gap-3 animate-in fade-in duration-150">
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)]">
                Shape
              </label>
              <select
                value={shape}
                onChange={(e) => setShape(e.target.value)}
                className={selectClass}
              >
                {SHAPE_TYPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Section Capacity"
              type="number"
              value={sectionCapacity}
              onChange={(e) => setSectionCapacity(e.target.value)}
              placeholder="Optional (total)"
            />
          </div>
        )}
      </div>

      {/* ================================================================ */}
      {/* ACTION BAR                                                       */}
      {/* ================================================================ */}
      <div className="flex items-center justify-between pt-2 border-t border-[var(--border-subtle)]">
        <div className="text-xs text-[var(--text-tertiary)]">
          {needsLocations && totalLocations > 0 && (
            <span>
              {totalLocations.toLocaleString()} locations will be generated
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {onCancel && (
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            type="button"
            onClick={handleCreate}
            disabled={!isValid}
            isLoading={
              isCreating ||
              addSectionMutation.isPending ||
              createSchemeMutation.isPending
            }
          >
            {isStandalone ? "Create Storage Area" : "Create Section"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SectionSetupWizard;
